import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { User, BookOpen, Save, Building2, GraduationCap, Users, Briefcase, Mail, Phone, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TeacherProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '',
    title: '', specialization: '', bio: '', office: '', office_hours: '', research_interests: '',
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/teachers/me/full-profile`);
      setProfile(data);
      setForm({
        first_name: data.user?.first_name || '',
        last_name: data.user?.last_name || '',
        phone: data.user?.phone || '',
        title: data.profile?.title || '',
        specialization: data.profile?.specialization || '',
        bio: data.profile?.bio || '',
        office: data.profile?.office || '',
        office_hours: data.profile?.office_hours || '',
        research_interests: data.profile?.research_interests || '',
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/teachers/me/profile`, form);
      toast.success('Profil mis a jour');
      load();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  const p = profile;
  const initials = `${form.first_name?.[0] || ''}${form.last_name?.[0] || ''}`.toUpperCase();

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="teacher-profile-page">
      <h1 className="text-xl font-bold flex items-center gap-2"><User className="w-5 h-5" /> Profil Enseignant</h1>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-teacher-profile"><User className="w-4 h-4 mr-1" /> Profil</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-teacher-courses"><BookOpen className="w-4 h-4 mr-1" /> Mes Cours ({p?.stats?.total_courses || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Card */}
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-2xl font-bold mx-auto mb-3">{initials}</div>
                <p className="font-semibold">{form.first_name} {form.last_name}</p>
                {form.title && <Badge variant="secondary" className="mt-1">{form.title}</Badge>}
                <p className="text-sm text-gray-400 mt-1">{p?.user?.email}</p>
                <Separator className="my-3" />
                <div className="text-left space-y-2 text-sm">
                  <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400" /><span>{p?.academic?.university?.name || '-'}</span></div>
                  <div className="flex items-center gap-2"><GraduationCap className="w-4 h-4 text-gray-400" /><span>{p?.academic?.faculty?.name || '-'}</span></div>
                  <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-gray-400" /><span>{p?.academic?.department?.name || '-'}</span></div>
                </div>
                <Separator className="my-3" />
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div><p className="text-xl font-bold text-blue-600">{p?.stats?.total_courses || 0}</p><p className="text-[10px] text-gray-400">Cours</p></div>
                  <div><p className="text-xl font-bold text-green-600">{p?.stats?.total_students || 0}</p><p className="text-[10px] text-gray-400">Etudiants</p></div>
                </div>
              </CardContent>
            </Card>

            {/* Edit Form */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3"><CardTitle className="text-base">Modifier le Profil</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Prenom</Label><Input value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))} data-testid="teacher-firstname" /></div>
                  <div><Label>Nom</Label><Input value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))} data-testid="teacher-lastname" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Telephone</Label><Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+243..." /></div>
                  <div><Label>Titre academique</Label><Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Prof., Dr., Ir., Ass..." data-testid="teacher-title" /></div>
                </div>
                <div><Label>Specialisation</Label><Input value={form.specialization} onChange={e => setForm(p => ({...p, specialization: e.target.value}))} placeholder="Genie Logiciel, IA, Droit Civil..." data-testid="teacher-specialization" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Bureau</Label><Input value={form.office} onChange={e => setForm(p => ({...p, office: e.target.value}))} placeholder="Bureau 204, Bat. B" /></div>
                  <div><Label>Heures de permanence</Label><Input value={form.office_hours} onChange={e => setForm(p => ({...p, office_hours: e.target.value}))} placeholder="Lundi 10h-12h" /></div>
                </div>
                <div><Label>Domaines de recherche</Label><Input value={form.research_interests} onChange={e => setForm(p => ({...p, research_interests: e.target.value}))} placeholder="Intelligence Artificielle, Machine Learning..." /></div>
                <div><Label>Biographie</Label>
                  <textarea className="flex w-full rounded-md border bg-transparent px-3 py-2 text-sm min-h-[80px]" value={form.bio} onChange={e => setForm(p => ({...p, bio: e.target.value}))} placeholder="Presentation professionnelle..." data-testid="teacher-bio" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={save} disabled={saving} data-testid="save-teacher-profile">
                    <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="courses">
          {(!p?.courses || p.courses.length === 0) ? (
            <Card><CardContent className="py-12 text-center text-gray-400 text-sm">Aucun cours assigne pour le moment.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {p.courses.map(c => (
                <Card key={c.id} data-testid={`teacher-course-${c.id}`}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-medium text-sm">{c.name}</p>
                        <p className="text-xs text-gray-400">{c.code} - {c.credits || 0} credits</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline">{c.enrollment_count || 0} etudiants</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
